import {
	CircularProgress,
	Typography,
	Checkbox,
	Grid,
	Paper,
	Skeleton,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { Box } from '@mui/system';
import {
	formatCurrency,
	findQuoteStatusByProductId,
	getReferralQuestions,
	getDeclinedQuestions,
	getProductValues,
} from '../../helper/constants';
import { StatusNumber } from '../../helper/statusNumber';
import { colours } from '../../shared/colors';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { Package, Product, QuoteProduct } from '../../shared/types/quotes';

const checkboxSX = {
	color: colours.darkGrey.main,
	'&.Mui-checked': {
		color: colours.darkGrey.main,
	},
	'&.Mui-disabled': {
		color: 'rgba(182, 182, 182, 1)',
		pointerEvents: 'auto',
		'&:hover': {
			backgroundColor: 'transparent',
		},
		cursor: 'not-allowed',
		'& .MuiSvgIcon-root .innerBox': {
			fill: '#eee',
		},
	},
	p: '4px',
};

/**
 * Component for displaying and managing quote summary products and packages.
 *
 * @param {Object} props - The component's props.
 * @param {Function} props.checkBoxHandler - A function to handle checkbox changes.
 * @returns {JSX.Element} - React JSX element representing the summary of quote products and packages
 */
const QuoteSummaryProducts = (props: any) => {
	const { currentQuotes, decisionTree, loadingProducts, quoteCheckbox } = useQuoteContext();

	const formik = props.options;

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.up('sm'));

	const productLimits = (
		item: any,
		checkboxProps: { checked?: boolean; disabled: boolean; onClick?: any }
	) => {
		const { excess, liability, premium } = getProductValues(item, currentQuotes, decisionTree);

		return (
			<Grid container spacing={1}>
				{checkboxProps.checked !== undefined && checkboxProps.onClick !== undefined && (
					<Grid item xs={1}>
						<Checkbox
							sx={checkboxSX}
							checked={checkboxProps?.checked}
							disabled={checkboxProps?.disabled}
							onClick={checkboxProps?.onClick}
							size='small'
						/>
					</Grid>
				)}
				<Grid item xs={checkboxProps ? 3 : 4}>
					<Typography variant='cardSubtitle2'>{item?.name}</Typography>
				</Grid>
				<Grid item xs={true} container spacing={1} sx={{ ...(isSmallScreen && { pl: 2 }) }}>
					<Grid item md={4} sm={12}>
						<Grid
							container
							direction='column'
							justifyContent='flex-start'
							textAlign='left'
						>
							<Grid item>
								<Typography
									variant='cardSubtitle2'
									textAlign='left'
									className='cardSummary_packageHeader'
								>
									Limit
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant='cardSubtitle1'>
									{loadingProducts ? (
										<Skeleton sx={{ maxWidth: '3em' }} />
									) : (
										formatCurrency(liability).split('.')[0]
									)}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					<Grid item md={4} sm={12}>
						<Grid
							container
							direction='column'
							justifyContent='flex-start'
							textAlign='left'
						>
							<Grid item>
								<Typography
									variant='cardSubtitle2'
									textAlign='left'
									className='cardSummary_packageHeader'
								>
									Retention
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant='cardSubtitle1'>
									{loadingProducts ? (
										<Skeleton sx={{ maxWidth: '3em' }} />
									) : (
										formatCurrency(excess).split('.')[0]
									)}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					<Grid item md={4} sm={12}>
						<Grid
							container
							direction='column'
							justifyContent='flex-start'
							textAlign='left'
						>
							<Grid item>
								<Typography
									variant='cardSubtitle2'
									textAlign='left'
									className='cardSummary_packageHeader'
								>
									Premium
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant='cardSubtitle1'>
									{loadingProducts ? (
										<Skeleton sx={{ maxWidth: '3em' }} />
									) : premium != null && typeof premium == 'string' ? (
										premium
									) : (
										formatCurrency(premium)
									)}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			{formik.values.products.map((item: Product) => {
				let locked = formik.values.locked.products.find((id: any) => id == item.id) != null;

				return (
					<div className='cardSummary_infoContainer' key={item?.id}>
						{productLimits(item, {
							...(quoteCheckbox && {
								checked:
									quoteCheckbox.find(
										(x: any) => x.id === item.id && x.packageId === 'PRODUCT'
									) == null,
							}),
							disabled:
								loadingProducts || (item?.isMainForPackage ?? false) || locked,
							...(props.checkBoxHandler && {
								onClick: () => props.checkBoxHandler(item?.id, 'PRODUCT'),
							}),
						})}
					</div>
				);
			})}
			{formik.values.packages.map((packageItem: Package) => {
				let locked =
					formik.values.locked.packages.find((id: any) => id == packageItem.id) != null;
				let products = packageItem.products; // ?? packageItem.quoteProducts ?? [];
				return (
					<div key={packageItem?.id}>
						<div className='cardSummary_infoContainer'>
							<Typography 
                            variant='subtitle1' 
                            className='cardSummary_packageHeader'
                            sx={{ px: '1em', py: '0.5em' }}
                            >
								{packageItem?.name}
							</Typography>
						</div>
						{products?.map((product) => {
							return (
								<div className='cardSummary_infoContainer' key={product?.id}>
									{productLimits(product, {
										...(quoteCheckbox && {
											checked:
												quoteCheckbox.find(
													(x: any) =>
														x.id === product?.id &&
														x.packageId === packageItem?.id
												) == null,
										}),
										disabled:
											loadingProducts ||
											(product?.isMainForPackage ?? false) ||
											locked,
										...(props.checkBoxHandler && {
											onClick: () =>
												props.checkBoxHandler(product?.id, packageItem?.id),
										}),
									})}
								</div>
							);
						})}
					</div>
				);
			})}
		</>
	);
};

export default QuoteSummaryProducts;
